import { isAutomated } from "../gitlabHtml";

const byCreatedAtAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

// The code line a diff thread is anchored to, as { path, line, ref }, or null
// for general and image comments. GitLab reports a new-side line against the
// HEAD SHA and a removed (old-side only) line against the start SHA.
export const getCodePosition = (discussion) => {
	const position = (discussion?.notes?.nodes || []).find(
		(note) => note?.position,
	)?.position;
	if (!position) return null;
	if (position.newLine && position.newPath && position.diffRefs?.headSha) {
		return {
			path: position.newPath,
			line: position.newLine,
			ref: position.diffRefs.headSha,
		};
	}
	if (position.oldLine && position.oldPath && position.diffRefs?.startSha) {
		return {
			path: position.oldPath,
			line: position.oldLine,
			ref: position.diffRefs.startSha,
		};
	}
	return null;
};

// Classify a single MR's discussions from the viewer's perspective. Returns a
// summary of threads whose latest human comment was written by someone other
// than the viewer (i.e. the viewer owes a reply), or null if none.
export const getCommentAttention = (discussions, viewerUsername) => {
	const unanswered = [];

	for (const discussion of discussions || []) {
		if (discussion?.resolvable && discussion.resolved) continue;

		const participants = (discussion?.notes?.nodes || [])
			.filter(
				(note) =>
					note && !note.system && note.author && !isAutomated(note.author),
			)
			.sort(byCreatedAtAsc);
		if (
			participants.length === 0 &&
			discussion?.notes?.pageInfo?.hasPreviousPage
		) {
			throw new Error("A comment thread was too long to classify completely");
		}
		const latest = participants.at(-1);

		if (latest && latest.author.username !== viewerUsername) {
			// The viewer's own last word in the thread, for context: what they are
			// being answered on. Null when they have not posted in it.
			const yours = participants.findLast(
				(note) => note.author.username === viewerUsername,
			);
			unanswered.push({
				id: discussion.id,
				createdAt: latest.createdAt,
				url: latest.url,
				body: latest.body || "",
				bodyHtml: latest.bodyHtml || "",
				author: latest.author,
				resolvable: discussion.resolvable,
				resolved: discussion.resolved,
				code: getCodePosition(discussion),
				yourLast: yours
					? { body: yours.body || "", createdAt: yours.createdAt }
					: null,
			});
		}
	}

	if (unanswered.length === 0) return null;

	unanswered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
	return {
		count: unanswered.length,
		latest: unanswered[0],
		threads: unanswered,
	};
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole Monday-to-Friday days elapsed since `date`, so a comment left on
// Friday afternoon is not "two days old" on Monday morning.
export const workingDaysSince = (date, now = new Date()) => {
	const start = new Date(date);
	if (Number.isNaN(start.getTime()) || start >= now) return 0;
	let days = 0;
	for (
		let cursor = new Date(start.getTime() + DAY_MS);
		cursor <= now;
		cursor = new Date(cursor.getTime() + DAY_MS)
	) {
		const weekday = cursor.getDay();
		if (weekday !== 0 && weekday !== 6) days++;
	}
	return days;
};
